import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Wand2, Code, Image } from 'lucide-react';
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'

const BackgroundRemoval = () => {
  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Background Removal API</h1>
        <p className="text-lg text-gray-600">
          Remove backgrounds from user-generated images programmatically
        </p>
      </div>

      {/* Overview Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5" />
            Overview
          </CardTitle>
          <CardDescription>
            Process user-uploaded images to remove backgrounds
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-500 mr-2 mt-0.5" />
              <div className="text-sm text-blue-700">
                The Background Removal API allows you to programmatically remove backgrounds from images 
                that have already been uploaded to Simple File Upload. Perfect for user-generated content 
                like profile photos, product images, or any image that needs background removal.
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium mb-2">Key Features</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
              <li>Process images already uploaded to your Simple File Upload account</li>
              <li>Returns a new image URL with transparent background</li>
              <li>Original image is preserved</li>
              <li>Processed images are automatically tagged with "removed-bg"</li>
              <li>Includes 20 background removals per month on the Basic plan</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* API Endpoint Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="w-5 h-5" />
            API Endpoint
          </CardTitle>
          <CardDescription>
            Remove background from an existing image
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="curl" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="curl">cURL</TabsTrigger>
              <TabsTrigger value="javascript">JavaScript</TabsTrigger>
              <TabsTrigger value="rails">Ruby on Rails</TabsTrigger>
            </TabsList>
            
            {/* cURL Tab */}
            <TabsContent value="curl" className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="default">POST</Badge>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">/api/v1/images/remove-background</code>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Request</h4>
                <SyntaxHighlighter
                  language="bash"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`curl -X POST https://app.simplefileupload.com/api/v1/images/remove-background \\
  -H "Content-Type: application/json" \\
  -u YOUR_API_KEY:YOUR_SECRET \\
  -d '{
    "url": "https://yoursubdomain.files-simplefileupload.com/path/to/image.jpg"
  }'`}
                </SyntaxHighlighter>
              </div>

              <div>
                <h4 className="font-medium mb-2">Response</h4>
                <SyntaxHighlighter
                  language="json"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`{
  "data": {
    "id": "123",
    "type": "files",
    "attributes": {
      "original_url": "https://yoursubdomain.files-simplefileupload.com/original.jpg",
      "processed_url": "https://yoursubdomain.files-simplefileupload.com/original_no_bg.png",
      "filename": "original_no_bg.png",
      "file_size": 2048576,
      "created_at": "2024-01-15T10:30:00Z",
      "tag": {
        "id": 45,
        "name": "removed-bg"
      }
    }
  }
}`}
                </SyntaxHighlighter>
              </div>
            </TabsContent>

            {/* JavaScript Tab */}
            <TabsContent value="javascript" className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                  <div className="text-sm text-yellow-700">
                    <strong>Security Notice:</strong> Never expose your API credentials in client-side code. 
                    Always make API calls from your backend server.
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Backend Server Implementation (Node.js/Express)</h4>
                <SyntaxHighlighter
                  language="javascript"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`// backend/routes/api.js
const express = require('express');
const router = express.Router();
const fetch = require('node-fetch');

// Store credentials securely in environment variables
const API_KEY = process.env.SIMPLE_FILE_UPLOAD_API_KEY;
const API_SECRET = process.env.SIMPLE_FILE_UPLOAD_API_SECRET;

router.post('/remove-background', async (req, res) => {
  try {
    const { imageUrl } = req.body;
    
    const response = await fetch('https://app.simplefileupload.com/api/v1/images/remove-background', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Basic ' + Buffer.from(API_KEY + ':' + API_SECRET).toString('base64')
      },
      body: JSON.stringify({ url: imageUrl })
    });
    
    const data = await response.json();
    res.json({ processedUrl: data.data.attributes.processed_url });
  } catch (error) {
    res.status(500).json({ error: 'Failed to process image' });
  }
});`}
                </SyntaxHighlighter>
              </div>

              <div className="mt-4">
                <h4 className="font-medium mb-2">Frontend Client Code</h4>
                <SyntaxHighlighter
                  language="javascript"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`// frontend/app.js
const removeBackground = async (imageUrl) => {
  const response = await fetch('/api/remove-background', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ imageUrl })
  });
  
  const data = await response.json();
  return data.processedUrl;
};

// Usage
const processedUrl = await removeBackground('https://yoursubdomain.files-simplefileupload.com/image.jpg');
console.log('Processed image:', processedUrl);`}
                </SyntaxHighlighter>
              </div>
            </TabsContent>

            {/* Ruby on Rails Tab */}
            <TabsContent value="rails" className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-500 mr-2 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <strong>Server-Side Only:</strong> This code should only be used in your Rails backend. 
                    Store credentials securely using Rails credentials or environment variables.
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Rails Controller Implementation</h4>
                <SyntaxHighlighter
                  language="ruby"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`# app/controllers/api/background_removals_controller.rb
class Api::BackgroundRemovalsController < ApplicationController
  # Skip CSRF for API endpoint if needed
  skip_before_action :verify_authenticity_token
  
  def create
    image_url = params[:image_url]
    
    begin
      response = HTTParty.post(
        'https://app.simplefileupload.com/api/v1/images/remove-background',
        body: { url: image_url }.to_json,
        headers: {
          'Content-Type' => 'application/json'
        },
        basic_auth: {
          username: Rails.application.credentials.simple_file_upload[:api_key],
          password: Rails.application.credentials.simple_file_upload[:api_secret]
        }
      )
      
      if response.success?
        processed_url = response.parsed_response.dig('data', 'attributes', 'processed_url')
        render json: { processed_url: processed_url }
      else
        render json: { error: 'Failed to process image' }, status: :unprocessable_entity
      end
    rescue => e
      Rails.logger.error "Background removal failed: #{e.message}"
      render json: { error: 'Failed to process image' }, status: :internal_server_error
    end
  end
end`}
                </SyntaxHighlighter>
              </div>

              <div className="mt-4">
                <h4 className="font-medium mb-2">Routes Configuration</h4>
                <SyntaxHighlighter
                  language="ruby"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`# config/routes.rb
namespace :api do
  resources :background_removals, only: [:create]
end`}
                </SyntaxHighlighter>
              </div>

              <div className="mt-4">
                <h4 className="font-medium mb-2">Storing Credentials</h4>
                <SyntaxHighlighter
                  language="bash"
                  style={atomOneDark}
                  customStyle={{
                    background: '#21252b',
                    padding: '1rem',
                    margin: 0,
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                >
{`# Edit Rails credentials
rails credentials:edit

# Add your Simple File Upload credentials:
simple_file_upload:
  api_key: your_api_key_here
  api_secret: your_api_secret_here`}
                </SyntaxHighlighter>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Workflow Example */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5" />
            Complete Workflow Example
          </CardTitle>
          <CardDescription>
            Upload an image and remove its background
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">Step 1: Upload the Image</h4>
            <SyntaxHighlighter
              language="bash"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            >
{`# Upload an image first
curl https://app.simplefileupload.com/api/v1/file \\
  -F "file=@/path/to/profile-photo.jpg" \\
  -F "tag=user-uploads" \\
  -u YOUR_API_KEY:YOUR_SECRET`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h4 className="font-medium mb-2">Step 2: Extract the CDN URL from Response</h4>
            <SyntaxHighlighter
              language="json"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            >
{`{
  "data": {
    "attributes": {
      "cdn-url": "https://yoursubdomain.files-simplefileupload.com/abc123/profile-photo.jpg"
    }
  }
}`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h4 className="font-medium mb-2">Step 3: Remove the Background</h4>
            <SyntaxHighlighter
              language="bash"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            >
{`# Use the CDN URL to remove background
curl -X POST https://app.simplefileupload.com/api/v1/images/remove-background \\
  -H "Content-Type: application/json" \\
  -u YOUR_API_KEY:YOUR_SECRET \\
  -d '{
    "url": "https://yoursubdomain.files-simplefileupload.com/abc123/profile-photo.jpg"
  }'`}
            </SyntaxHighlighter>
          </div>
        </CardContent>
      </Card>

      {/* Important Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Important Notes</CardTitle>
          <CardDescription>
            Things to keep in mind when using the Background Removal API
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">Usage Limits</p>
                  <p>Background removal counts against your monthly quota. Basic plans include 20 removals per month.</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2">Best Practices</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                <li>Only process images that actually need background removal to conserve your monthly quota</li>
                <li>The API works best with images that have clear subjects and contrasting backgrounds</li>
                <li>Both original and processed images count toward your storage quota</li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium mb-2">Error Handling</h4>
              <p className="text-sm text-gray-600 mb-2">Common error responses:</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">404</code> - Image not found (URL doesn't exist in your account)</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">422</code> - Invalid image format or processing error</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">429</code> - Monthly quota exceeded</li>
                <li><code className="bg-gray-100 px-1 py-0.5 rounded">401</code> - Invalid API credentials</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BackgroundRemoval;